import { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import {
  FileText,
  Scissors,
  Eye,
  EyeOff,
  RotateCcw,
  RotateCw,
  Plus,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'react-toastify';
import { Separator } from './ui/separator';

// Worker (keep your setup; this works for react-pdf@^7 with pdfjs v3/4+)
try {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
} catch {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.5.3.93.min.mjs';
}

/**
 * Props:
 * - pdfFile:          currently selected file (File/Blob or metadata object)
 * - allFiles:         full array from MultiFileManager (can contain File or metadata objects)
 * - selectedFileIndex: current single selected index
 * - selectedFileIndices?: number[] (OPTIONAL multi-select indices from parent)
 * - onClose, onSavePages: callbacks
 */
export function PDFSplitter({
  pdfFile,
  allFiles = [],
  selectedFileIndex = 0,
  selectedFileIndices = null,
  onClose,
  onSavePages,
}) {
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [previewMode, setPreviewMode] = useState(true);
  const [pageRotations, setPageRotations] = useState({});
  const [pdfEntries, setPdfEntries] = useState([]); // [{ id, name, src, file?, needsRevoke, isMain, isAdditional, numPages }]
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // --------- helpers to resolve sources from your unified file objects ----------
  const _makeObjectUrl = (blob) => {
    const url = URL.createObjectURL(blob);
    return { url, needsRevoke: true };
  };

const _coerceToEntry = (item, idx, { isMain = false, isAdditional = false } = {}) => {
  console.log('PDFSplitter _coerceToEntry received:', item); // ← Add this debug
  
  // Accept raw File/Blob
  if (item instanceof File || item instanceof Blob) {
    const name = item.name || `Document ${idx + 1}.pdf`;
    // Prefer existing blobUrl if you attached it
    if (item.blobUrl) {
      console.log('Using item.blobUrl:', item.blobUrl); // ← Add this debug
      return { id: idx, name, src: item.blobUrl, file: item, needsRevoke: false, isMain, isAdditional, numPages: null };
    }
    const { url, needsRevoke } = _makeObjectUrl(item);
    console.log('Created new blob URL:', url); // ← Add this debug
    return { id: idx, name, src: url, file: item, needsRevoke, isMain, isAdditional, numPages: null };
  }

  // Accept your metadata objects
  // Try blobUrl first, then downloadUrl (OneDrive), then url
  const name = item?.name || item?.fileName || `Document ${idx + 1}.pdf`;
  if (item?.blobUrl) {
    console.log('Using metadata blobUrl:', item.blobUrl); // ← Add this debug
    return { id: idx, name, src: item.blobUrl, blobUrl: item.blobUrl, file: item, needsRevoke: false, isMain, isAdditional, numPages: null };
  }
  if (item?.downloadUrl) {
    console.log('Using metadata downloadUrl:', item.downloadUrl); // ← Add this debug
    return { id: idx, name, src: item.downloadUrl, file: item, needsRevoke: false, isMain, isAdditional, numPages: null };
  }
  if (item?.url && typeof item.url === 'string') {
    console.log('Using metadata url:', item.url); // ← Add this debug
    return { id: idx, name, src: item.url, blobUrl: item.url, file: item, needsRevoke: false, isMain, isAdditional, numPages: null };
  }

  // Fallback: cannot render
  console.warn('Unsupported file item for PDFSplitter:', item);
  return null;
};

  // Build initial list from MultiFileManager selection
  useEffect(() => {
    const buildInitial = async () => {
      setLoadingPdfs(true);
      try {        
        let initialList = [];
        const indices = Array.isArray(selectedFileIndices) && selectedFileIndices.length
          ? selectedFileIndices
          : [selectedFileIndex];


        if (allFiles.length > 0 && indices.length > 0) {
          initialList = indices
            .map((i, localIdx) => _coerceToEntry(allFiles[i], i, { isMain: i === selectedFileIndex, isAdditional: i !== selectedFileIndex  }))
            .filter(Boolean);
        } else if (pdfFile) {
          // Fallback when you only passed pdfFile
          initialList = [_coerceToEntry(pdfFile, 0, { isMain: true })].filter(Boolean);
        }

        setPdfEntries(initialList);
      } finally {
        setLoadingPdfs(false);
      }
    };

    buildInitial();

    return () => {
      // Revoke only URLs we created here (not parent-owned blobUrls)
      setPdfEntries((prev) => {
        prev?.forEach((e) => {
          if (e?.needsRevoke && e?.src) {
            try { URL.revokeObjectURL(e.src); } catch {}
          }
        });
        return prev;
      });
    };
  }, [allFiles?.length, selectedFileIndex, selectedFileIndices?.length, pdfFile]);

  // react-pdf load success per document
  const onDocumentLoadSuccess = ({ numPages }, pdfId) => {
    setPdfEntries((prev) =>
      prev.map((e) => (e.id === pdfId ? { ...e, numPages } : e))
    );

    // Auto-select all pages of each document when it loads (you can change this behavior)
    const pageIds = Array.from({ length: numPages }, (_, i) => `pdf${pdfId}-page${i + 1}`);
    setSelectedPages((prev) => new Set([...prev, ...pageIds]));
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    toast('Failed to load a PDF. Check if the file/URL is valid.', { className: 'error-toast', autoClose: 3000, closeOnClick: true });
  };

  const togglePageSelection = (pageId) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      next.has(pageId) ? next.delete(pageId) : next.add(pageId);
      return next;
    });
  };

  const selectAllPages = () => {
    const allIds = [];
    pdfEntries.forEach((e) => {
      if (e.numPages) {
        for (let i = 1; i <= e.numPages; i++) allIds.push(`pdf${e.id}-page${i}`);
      }
    });
    setSelectedPages(new Set(allIds));
  };

  const deselectAllPages = () => setSelectedPages(new Set());

  const rotatePage = (pageId, dir) => {
    setPageRotations((prev) => ({
      ...prev,
      [pageId]: ((prev[pageId] || 0) + (dir === 'cw' ? 90 : -90)) % 360,
    }));
  };

  // Add more PDFs inside the modal (optional)
  const handleAdditionalFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    const pdfFiles = files.filter((f) => f.type === 'application/pdf');

    if (pdfFiles.length !== files.length) {
      toast('Only PDF files can be added.', { className: 'warning-toast', autoClose: 3000, closeOnClick: true });
    }
    if (!pdfFiles.length) return;

    const newEntries = pdfFiles
      .map((file, idx) => _coerceToEntry(file, (pdfEntries.length + idx), { isAdditional: true }))
      .filter(Boolean);

    setPdfEntries((prev) => [...prev, ...newEntries]);
    event.target.value = '';
  };

  const removeDocument = (pdfId) => {
    const entry = pdfEntries.find((e) => e.id === pdfId);
    if (!entry) return;

    if (entry.needsRevoke && entry.src) {
      try { URL.revokeObjectURL(entry.src); } catch {}
    }

    setPdfEntries((prev) => prev.filter((e) => e.id !== pdfId));

    // Remove selected pages & rotations for this doc
    setSelectedPages((prev) => {
      const next = new Set(prev);
      for (const id of prev) if (id.startsWith(`pdf${pdfId}-`)) next.delete(id);
      return next;
    });
    setPageRotations((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((k) => { if (k.startsWith(`pdf${pdfId}-`)) delete copy[k]; });
      return copy;
    });
  };

// Replace the createCombinedPDF function with this corrected version:
const createCombinedPDF = async () => {
  setIsProcessing(true);
  try {
    const merged = await PDFDocument.create();

    // Sort selected page IDs by doc id then page num
    const sortedPageIds = Array.from(selectedPages).sort((a, b) => {
      const [aDoc, aPg] = a.split('-');
      const [bDoc, bPg] = b.split('-');
      const aId = parseInt(aDoc.replace('pdf', ''), 10);
      const bId = parseInt(bDoc.replace('pdf', ''), 10);
      const aNum = parseInt(aPg.replace('page', ''), 10);
      const bNum = parseInt(bPg.replace('page', ''), 10);
      return aId === bId ? aNum - bNum : aId - bId;
    });

    // Cache loaded docs by id
    const loaded = {};

    const selectedByDoc = {}; // { docId: Set(pageNums) }
    for (const pageId of sortedPageIds) {
      const [docPart, pagePart] = pageId.split('-');
      const docId = parseInt(docPart.replace('pdf', ''), 10);
      const pageNum = parseInt(pagePart.replace('page', ''), 10);

      if(!selectedByDoc[docId]) selectedByDoc[docId] = new Set();
      selectedByDoc[docId].add(pageNum);
    }

    for (const pageId of sortedPageIds) {
      const [docPart, pagePart] = pageId.split('-');
      const docId = parseInt(docPart.replace('pdf', ''), 10);
      const pageNum = parseInt(pagePart.replace('page', ''), 10);      
      
      let entry = pdfEntries.find((e) => e.id === docId);
      if (!entry) continue;

      // Load the source document if not already loaded
      if (!loaded[docId]) {
        let bytes;
        if (entry.file && typeof entry.file.arrayBuffer === 'function') {
          bytes = await entry.file.arrayBuffer();
        } else if (entry.src) {
          const res = await fetch(entry.src);
          if (!res.ok) throw new Error(`Fetch failed for ${entry.name}: ${res.status}`);
          bytes = await res.arrayBuffer();
        } else {
          console.warn('No source to load PDF for entry:', entry);
          continue;
        }
        loaded[docId] = await PDFDocument.load(bytes);
      }

      // Copy the page from source to merged document
      const sourcePdf = loaded[docId];
      const [copiedPage] = await merged.copyPages(sourcePdf, [pageNum - 1]); // ← Fix: copy TO merged FROM source
      
      // Apply rotation if any
      const rotation = pageRotations[pageId] || 0;
      if (rotation) {
        copiedPage.setRotation({ angle: rotation });
      }
      
      // Add the copied page to merged document
      merged.addPage(copiedPage);
    }

    const pdfBytes = await merged.save();

    // Create a simple filename (will be renamed later when submitting)
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `New_Invoice_Draft_${timestamp}.pdf`;
    const newFile = new File([pdfBytes], filename, { type: 'application/pdf' });

    // Create blob URL for preview
    const blobUrl = URL.createObjectURL(newFile);

    console.log('Created new PDF:', {
      filename,
      pageCount: merged.getPageCount(),
      size: pdfBytes.length
    });

    // ============================================================
    // NOW CREATE THE UNSELECTED PAGES PDFs (PART FILES)
    // ============================================================
    
    const partFiles = [];

    const docIdsUsed = new Set();
    for (const pageId of selectedPages) {
      const [docPart] = pageId.split('-');
      const docId = parseInt(docPart.replace('pdf', ''), 10);
      docIdsUsed.add(docId);
    }

    for(const entry of pdfEntries) {
      const docId = entry.id;
      const totalPages = entry.numPages;

      if (!totalPages) continue;

      const selectedPagesForDoc = selectedByDoc[docId] || new Set();

      const unselectedPages = [];
      for (let i = 1; i <= totalPages; i++) {
        if (!selectedPagesForDoc.has(i)) unselectedPages.push(i);
      }

    if (unselectedPages.length === 0) continue;

    console.log(`Creating part file for ${entry.name} with ${unselectedPages.length} unselected pages.`);

    const partPdf = await PDFDocument.create();
    const sourcePdf = loaded[docId];

    for (const pageNum of unselectedPages) {
      const [copiedPage] = await partPdf.copyPages(sourcePdf, [pageNum - 1]);

      const pageId = `pdf${docId}-page${pageNum}`;
      const rotation = pageRotations[pageId] || 0;
      if (rotation) {
        copiedPage.setRotation({ angle: rotation });
      }

      partPdf.addPage(copiedPage);
    }

    const partPdfBytes = await partPdf.save();

    const originalName = entry.name;
    const nameWithoutExt = originalName.replace(/\.pdf$/i, '');

    const partMatch = nameWithoutExt.match(/_PART(\d+)$/);
    let partNumber = 1;

    if (partMatch) {
      const existingPartNum = parseInt(partMatch[1], 10);
      partNumber = existingPartNum + 1;

      const baseNameWithoutPart = nameWithoutExt.replace(/_PART\d+$/, '');
      var partFilename = `${baseNameWithoutPart}_PART${partNumber}.pdf`;
    } else {
      var partFilename = `${nameWithoutExt}_PART1.pdf`;
    }

    const partFile = new File([partPdfBytes], partFilename, { type: 'application/pdf' });

    partFiles.push({
      file: partFile,
      originalFileName: entry.name,
      originalFileId: entry.file?.id,
      pageCount: partPdf.getPageCount(),
      unselectedPageNumbers: unselectedPages,
    });
  }

    toast(
      <span>
        New PDF created with{" "}
      <span style={{ fontWeight: 700, color: "#05603A" }}>{merged.getPageCount()}</span>
      {" "}
        selected pages!
      </span>,
      { className: "success-toast", autoClose: 3000, closeOnClick: true }
    );

const originalFileNames = Array.from(docIdsUsed)
  .map(docId => {
    const entry = pdfEntries.find(e => e.id === docId);
    if(!entry) return null;
    if(entry.isTemporary || entry.name.startsWith('New_Invoice_Draft_')) return null; // Skip temporary files
    return entry.name;
  })
  .filter(Boolean);

    // Pass the new file back to parent with metadata
    onSavePages?.({
      selectedPages: Array.from(selectedPages),
      totalPages: merged.getPageCount(),
      newPdfFile: newFile,
      newPdfBlobUrl: blobUrl,
      originalFileNames,
      partFiles: partFiles,
    });
    console.log('Uploading with ORIGINALFILENAMES:', originalFileNames);

    // Close the splitter after creating the PDF
    onClose?.();

  } catch (err) {
    console.error('Error creating PDF:', err);
    toast('Failed to create PDF. Please try again.', { className: 'error-toast', autoClose: 3000, closeOnClick: true });
  } finally {
    setIsProcessing(false);
  }
};

  const PageThumbnail = ({ pageNumber, pageId, pdfId, fileName }) => (
    <div className="relative border rounded-lg overflow-hidden bg-white">
      <div className="absolute top-2 left-2 z-10">
        <Checkbox
          checked={selectedPages.has(pageId)}
          onCheckedChange={() => togglePageSelection(pageId)}
          className="bg-white"
        />
      </div>
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <Button size="sm" variant="secondary" onClick={() => rotatePage(pageId, 'ccw')} className="h-6 w-6 p-0">
          <RotateCcw className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="secondary" onClick={() => rotatePage(pageId, 'cw')} className="h-6 w-6 p-0">
          <RotateCw className="h-3 w-3" />
        </Button>
      </div>
      <Page
        pageNumber={pageNumber}
        width={200}
        rotate={pageRotations[pageId] || 0}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
      <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white text-xs p-1 text-center">
        <div>Page {pageNumber}</div>
        <div className="text-gray-300 truncate" title={fileName}>
          {fileName.length > 15 ? fileName.substring(0, 12) + '...' : fileName}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl h-[90vh] flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scissors className="h-5 w-5" />
                PDF Splitter & Merger
              </CardTitle>
              <CardDescription>Select pages from your **selected documents**. You can still add more here if needed.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <Badge variant="outline">
              {pdfEntries.reduce((acc, e) => acc + (e.numPages || 0), 0)} total pages
            </Badge>
            <Badge variant="secondary">{selectedPages.size} selected</Badge>
            <Badge variant="outline">{pdfEntries.length} documents</Badge>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={selectAllPages} disabled={!pdfEntries.length}>
              Select All
            </Button>
            <Button size="sm" variant="outline" onClick={deselectAllPages}>
              Deselect All
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreviewMode((v) => !v)}>
              {previewMode ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {previewMode ? 'Hide Preview' : 'Show Preview'}
            </Button>
            {/* <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Plus className="h-4 w-4 mr-2" />
              Add More PDFs
            </Button> */}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden flex flex-col gap-4">
          {loadingPdfs && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-current border-t-transparent mx-auto mb-2" />
              Loading PDFs...
            </div>
          )}

          {!loadingPdfs && pdfEntries.length > 0 && (
            <div className="flex-1 overflow-y-auto space-y-6">
              {pdfEntries.map((entry, i) => (
                <div key={entry.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{entry.name}</h3>
                      {entry.isMain && <Badge variant="default" className="text-xs">Main</Badge>}
                      {entry.isAdditional && <Badge variant="secondary" className="text-xs">Added</Badge>}
                      {entry.numPages && <Badge variant="outline" className="text-xs">{entry.numPages} pages</Badge>}
                    </div>
                    {entry.isAdditional && (
                      <Button size="sm" variant="ghost" onClick={() => removeDocument(entry.id)} className="h-6 w-6 p-0">
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  <Document
                    file={entry.blobUrl || entry.src}  // File/Blob first; else URL string
                    onLoadSuccess={(result) => onDocumentLoadSuccess(result, entry.id)}
                    onLoadError={(error) => {
                      console.error('PDF.js Document load error for entry:', entry, 'Error:', error); // ← Add debug
                      onDocumentLoadError(error);
                    }}
                    loading={<div className="text-sm text-muted-foreground py-4">Loading pages...</div>}
                    error={<div className="text-sm text-red-500 py-4">Failed to load this PDF</div>}
                  >
                    {previewMode && entry.numPages && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {Array.from({ length: entry.numPages }, (_, n) => n + 1).map((pageNum) => {
                          const pageId = `pdf${entry.id}-page${pageNum}`;
                          return (
                            <PageThumbnail
                              key={pageId}
                              pageNumber={pageNum}
                              pageId={pageId}
                              pdfId={entry.id}
                              fileName={entry.name}
                            />
                          );
                        })}
                      </div>
                    )}

                    {!previewMode && entry.numPages && (
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {Array.from({ length: entry.numPages }, (_, n) => n + 1).map((pageNum) => {
                          const pageId = `pdf${entry.id}-page${pageNum}`;
                          return (
                            <div key={pageId} className="flex items-center space-x-2 p-2 border rounded">
                              <Checkbox
                                checked={selectedPages.has(pageId)}
                                onCheckedChange={() => togglePageSelection(pageId)}
                              />
                              <span className="text-sm">Page {pageNum}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Document>

                  {i < pdfEntries.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          )}
        </CardContent>

        <div className="flex-shrink-0 p-6 border-t bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {selectedPages.size > 0
                ? `Ready to create PDF from ${selectedPages.size} selected pages`
                : 'Select pages from any document to create a combined PDF'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={createCombinedPDF} disabled={selectedPages.size === 0 || isProcessing}>
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Create PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf"
          onChange={handleAdditionalFileUpload}
          className="hidden"
        />
      </Card>
    </div>
  );
}
